import { Link } from "react-router-dom";
import { Instagram, Mail, MessageCircle } from "lucide-react";
import Logo from "@/components/Logo";
import { useContent } from "@/context/ContentContext";
import { useShop } from "@/context/ShopContext";

const Footer = () => {
  const { c } = useContent();
  const { categories } = useShop();
  const whatsapp = c("contact_whatsapp", "9582332128").replace(/\D/g, "");
  const waLink = `https://wa.me/${whatsapp.length === 10 ? `91${whatsapp}` : whatsapp}`;
  const email = c("contact_email", "riaascollective@gmail.com");
  const instagram = c("contact_instagram", "https://www.instagram.com/riaascollective/");
  const igHandle = "@" + instagram.replace(/\/$/, "").split("/").pop();

  return (
    <footer className="border-t border-linen bg-espresso text-cream" data-testid="site-footer">
      <div className="mx-auto grid max-w-7xl gap-12 px-5 py-16 sm:px-8 md:grid-cols-12">
        <div className="md:col-span-4">
          <Logo onDark />
          <p className="mt-5 max-w-sm text-sm leading-relaxed text-cream/60">
            {c("footer_blurb", "Handmade ceramic crockery, planters and home decor — mugs, glasses, salt & pepper sets and sculptural pots, made with food-safe mineral glazes and delivered across India.")}
          </p>
        </div>
        <div className="md:col-span-2">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ochre">Shop</p>
          <ul className="mt-4 space-y-2.5 text-sm text-cream/70">
            <li><Link to="/shop" className="transition-colors hover:text-terra-light">All Ceramics</Link></li>
            {(categories.length
              ? categories
              : [{ name: "Mugs", label: "Mugs & Cups" }, { name: "Planters", label: "Planters & Pots" }, { name: "Decor Items", label: "Decor Items" }]
            ).map((cat) => (
              <li key={cat.name}><Link to={`/shop?cat=${encodeURIComponent(cat.name)}`} className="transition-colors hover:text-terra-light">{cat.label}</Link></li>
            ))}
          </ul>
        </div>
        <div className="md:col-span-2">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ochre">Help</p>
          <ul className="mt-4 space-y-2.5 text-sm text-cream/70">
            <li><Link to="/terms" className="transition-colors hover:text-terra-light">Terms &amp; Conditions</Link></li>
            <li><Link to="/returns" className="transition-colors hover:text-terra-light">Returns &amp; Exchange</Link></li>
            <li><Link to="/wholesale" className="transition-colors hover:text-terra-light">Wholesale &amp; Bulk Orders</Link></li>
            <li><Link to="/admin" className="transition-colors hover:text-terra-light">Admin Portal</Link></li>
          </ul>
        </div>
        <div className="md:col-span-2">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ochre">Contact</p>
          <ul className="mt-4 space-y-2.5 text-sm text-cream/70">
            <li>
              <a href={waLink} target="_blank" rel="noreferrer" data-testid="footer-whatsapp-link" className="flex items-center gap-2 transition-colors hover:text-terra-light">
                <MessageCircle className="h-3.5 w-3.5 text-ochre" /> {whatsapp}
              </a>
            </li>
            <li>
              <a href={`mailto:${email}`} data-testid="footer-email-link" className="flex items-center gap-2 break-all transition-colors hover:text-terra-light">
                <Mail className="h-3.5 w-3.5 shrink-0 text-ochre" /> {email}
              </a>
            </li>
            <li>
              <a href={instagram} target="_blank" rel="noreferrer" data-testid="footer-instagram-link" className="flex items-center gap-2 transition-colors hover:text-terra-light">
                <Instagram className="h-3.5 w-3.5 text-ochre" /> {igHandle}
              </a>
            </li>
          </ul>
        </div>
        <div className="md:col-span-2">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ochre">Good to know</p>
          <ul className="mt-4 space-y-2.5 text-sm text-cream/70">
            <li>{c("footer_know1", "Free insured shipping over ₹2,500")}</li>
            <li>{c("footer_know2", "Secure, crushproof packaging")}</li>
            <li>{c("footer_know3", "Easy 7-day returns on damage")}</li>
            <li>{c("footer_know4", "Secure payments via Razorpay")}</li>
          </ul>
        </div>
      </div>
      <div className="border-t border-cream/10">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-3 px-5 py-6 font-mono text-[10px] uppercase tracking-[0.22em] text-cream/40 sm:flex-row sm:px-8">
          <span>© {new Date().getFullYear()} Riaas Collective</span>
          <span>Handmade ceramics, delivered to your door</span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
